import React, { useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneTilt, CheckCircle, Phone, Envelope, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "../../context/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";

const empty = {
  name: "", email: "", phone: "", property_type: "", city: "", surface: "",
  frequency: "", services: [], message: "",
};

export default function QuoteForm() {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.property_type) {
      toast.error(t.quote.error);
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(empty);
      toast.success(t.quote.success);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-24 sm:py-32 bg-sage">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        {/* left copy */}
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-leaf">{t.quote.overline}</span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-forest">{t.quote.title}</h2>
          <p className="mt-5 text-forest/70 text-lg">{t.quote.sub}</p>
          <div className="mt-8 space-y-4">
            <a href="tel:3435436442" className="flex items-center gap-4 rounded-2xl bg-white border border-forest/10 p-5 hover:border-forest/30 transition-colors" data-testid="quote-phone">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-forest text-white"><Phone size={20} weight="fill" /></div>
              <div><div className="text-xs uppercase tracking-widest text-forest/50 font-bold">Tel</div><div className="font-bold text-forest">343 543 6442</div></div>
            </a>
            <a href="mailto:Kelsyhousecleaning@gmail.com" className="flex items-center gap-4 rounded-2xl bg-white border border-forest/10 p-5 hover:border-forest/30 transition-colors" data-testid="quote-email">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-gold text-white"><Envelope size={20} weight="fill" /></div>
              <div><div className="text-xs uppercase tracking-widest text-forest/50 font-bold">Email</div><div className="font-bold text-forest text-sm">Kelsyhousecleaning@gmail.com</div></div>
            </a>
          </div>
        </div>

        {/* form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 rounded-3xl bg-white border border-forest/10 p-7 sm:p-10 shadow-sm"
        >
          {done ? (
            <div className="text-center py-16" data-testid="quote-success">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-leaf/15 text-leaf">
                <CheckCircle size={36} weight="fill" />
              </div>
              <p className="mt-6 font-serif text-2xl text-forest max-w-sm mx-auto">{t.quote.success}</p>
              <button onClick={() => setDone(false)} className="mt-6 text-sm font-bold text-leaf underline" data-testid="quote-reset">
                {t.quote.overline}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="q-name">{t.quote.name} *</Label>
                  <Input id="q-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl h-11" data-testid="quote-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="q-email">{t.quote.email} *</Label>
                  <Input id="q-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-xl h-11" data-testid="quote-email-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="q-phone">{t.quote.phone}</Label>
                  <Input id="q-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl h-11" data-testid="quote-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t.quote.city}</Label>
                  <Select value={form.city} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger className="rounded-xl h-11" data-testid="quote-city"><SelectValue placeholder="Ottawa / Gatineau" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ottawa">Ottawa</SelectItem>
                      <SelectItem value="Gatineau">Gatineau</SelectItem>
                      <SelectItem value="Autre">Autre / Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>{t.quote.propertyType} *</Label>
                  <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                    <SelectTrigger className="rounded-xl h-11" data-testid="quote-property"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {t.quote.propertyOptions.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.quote.frequency}</Label>
                  <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                    <SelectTrigger className="rounded-xl h-11" data-testid="quote-frequency"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {t.quote.frequencyOptions.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="q-surface">{t.quote.surface}</Label>
                <Input id="q-surface" value={form.surface} onChange={(e) => set("surface", e.target.value)} className="rounded-xl h-11" data-testid="quote-surface" />
              </div>

              <div className="space-y-3">
                <Label>{t.quote.services}</Label>
                <div className="flex flex-wrap gap-2">
                  {t.quote.serviceOptions.map((s) => {
                    const active = form.services.includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        onClick={() => toggleService(s)}
                        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                          active ? "bg-forest text-white border-forest" : "border-forest/20 text-forest/70 hover:border-forest"
                        }`}
                        data-testid={`quote-service-${s}`}
                      >
                        <span className={`grid h-4 w-4 place-items-center rounded-[4px] border ${active ? "bg-white border-white text-forest" : "border-forest/40"}`}>
                          {active && <Check size={11} weight="bold" />}
                        </span>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="q-msg">{t.quote.message}</Label>
                <Textarea id="q-msg" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-xl" data-testid="quote-message" />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest px-7 py-4 text-sm font-bold text-white hover:bg-forest-dark transition-colors disabled:opacity-60"
                data-testid="quote-submit"
              >
                <PaperPlaneTilt size={18} weight="fill" />
                {loading ? t.quote.sending : t.quote.submit}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
